import React, { useState, useRef } from 'react';

interface MessageComposerProps {
  onSend: (content: string, files: File[]) => Promise<void>;
}

const MessageComposer: React.FC<MessageComposerProps> = ({ onSend }) => {
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'video/mp4', 'video/quicktime'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 5;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of selectedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: type non autorise (JPG, PNG, PDF, MP4, MOV)`);
        continue;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: trop volumineux (max 10 Mo)`);
        continue;
      }
      
      if (files.length + validFiles.length >= MAX_FILES) {
        errors.push(`Maximum ${MAX_FILES} fichiers`);
        break;
      }
      
      validFiles.push(file);
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!content.trim() && files.length === 0) {
      return;
    }

    const trimmed = content.trim();

    if (trimmed.length > 0 && trimmed.length < 10) {
      alert('Le message doit contenir au moins 10 caracteres');
      return;
    }

    if (trimmed.length > 2000) {
      alert('Le message ne doit pas depasser 2000 caracteres');
      return;
    }

    setSending(true);
    try {
      await onSend(trimmed || 'Fichier joint', files);
      setContent('');
      setFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="border-t bg-white px-6 py-4">
      {files.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 rounded px-3 py-2 text-sm"
            >
              <span className="mr-2">📎</span>
              <span className="mr-2 truncate max-w-[150px]">{file.name}</span>
              <span className="text-gray-500 text-xs mr-2">({formatFileSize(file.size)})</span>
              <button
                onClick={() => removeFile(index)}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end space-x-2">
        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Répondre à l’hôtel... (min 10 car.)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
            disabled={sending}
          />
          <div className="text-xs text-gray-500 mt-1">
            {content.length}/2000 caracteres
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || files.length >= MAX_FILES}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Joindre un fichier"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>

          <button
            onClick={handleSend}
            disabled={sending || (!content.trim() && files.length === 0)}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.pdf,.mp4,.mov"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default MessageComposer;
